import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import SummaryApi from '../common';
import Swal from 'sweetalert2';
import { FaPlus, FaTimes, FaImage, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaEdit } from 'react-icons/fa';

Modal.setAppElement('#root');

// ==========================================
// 1. The Form Component (Handles Create & Update)
// ==========================================
const NotificationForm = ({ onClose, onSuccess, initialData }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_image: false,
    is_active: true,
  });

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // Helper to construct image URL for preview
  const getImageUrl = (path) => {
    if (!path) return null;
    const baseUrl = SummaryApi.notifictionImg.url; 
    let fullUrl = `${baseUrl}/${path}`;
    return fullUrl.replace('images/images/', 'images/');
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return { start: '', end: '' };
    if (dateStr.includes('From') && dateStr.includes('to')) {
        const cleanStr = dateStr.replace('From ', '');
        const parts = cleanStr.split(' to ');
        return { start: parts[0] || '', end: parts[1] || '' };
    }
    return { start: dateStr, end: '' };
  };

  // ==========================================
  // UPDATED USE EFFECT (THE FIX)
  // ==========================================
  useEffect(() => {
    if (initialData) {
      const { start, end } = parseDateString(initialData.notification_date);
      
      // Robust check for image path
      const hasImagePath = initialData.notification_image && initialData.notification_image.trim() !== "";

      setFormData({
        title: initialData.title || '',
        description: (initialData.description || '').substring(0, 100),
        // Ensure checkbox is checked if image path exists
        is_image: (initialData.is_image == 1 || initialData.is_image === true) || hasImagePath,
        is_active: (initialData.is_active == 1 || initialData.is_active === true),
      });
      
      setStartDate(start);
      setEndDate(end);

      if (hasImagePath) {
        setPreviewUrl(getImageUrl(initialData.notification_image));
      }
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'description' && value.length > 100) return;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setFormData(prev => ({ ...prev, is_image: true }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.description.length > 100) {
        Swal.fire({
            icon: 'warning',
            title: 'Limit Exceeded',
            text: "Description cannot exceed 100 characters",
            confirmButtonColor: '#3085d6',
        });
        return;
    }

    setLoading(true);

    try {
      const dataPayload = new FormData();
      
      if (initialData) {
        dataPayload.append('notification_id', initialData.notification_id);
      }

      dataPayload.append('title', formData.title);
      dataPayload.append('description', formData.description);

      let finalDateString = startDate;
      if (startDate && endDate) {
        finalDateString = `From ${startDate} to ${endDate}`;
      } else if (startDate) {
        finalDateString = startDate;
      }
      dataPayload.append('notification_date', finalDateString);
      
      dataPayload.append('is_image', formData.is_image ? '1' : '0'); 
      dataPayload.append('is_active', formData.is_active ? '1' : '0'); 

      if (imageFile) {
        dataPayload.append('notification_image', imageFile);
      }

      const apiConfig = initialData ? SummaryApi.updateNotification : SummaryApi.createNotification;

      const response = await fetch(apiConfig.url, {
        method: "POST", 
        body: dataPayload, 
        headers: {
            Authorization: "Bearer sandeep_university_916",
        },
      });

      const result = await response.json();

      if (result.status) {
        Swal.fire({
          icon: 'success',
          title: initialData ? 'Updated!' : 'Created!',
          text: initialData ? 'Notification Updated Successfully!' : 'Notification Created Successfully!',
          timer: 2000,
          showConfirmButton: false
        });
        onSuccess(); 
        onClose();   
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Operation Failed',
          text: result.message || 'Action failed.',
           timer: 2000,
          showConfirmButton: false
        });
      }

    } catch (error) {
      console.error("Network Error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Something went wrong connecting to the server.',
        confirmButtonColor: '#3085d6',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg">
      <div className="flex justify-between items-center p-4 border-b">
         <h2 className="text-xl font-bold text-gray-800">
            {initialData ? 'Edit Notification' : 'Create Notification'}
         </h2>
         <button onClick={onClose} className="text-gray-500 hover:text-red-500">
            <FaTimes size={20} />
         </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter title"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
             Description <span className="text-xs text-gray-400 font-normal">(Max 100 chars)</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter details..."
            rows="3"
            required
            maxLength={100} 
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
          ></textarea>
          <div className={`text-xs text-right mt-1 ${formData.description.length >= 100 ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
            {formData.description.length}/100 characters
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
              min={startDate}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-600"
            />
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
           <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Set Active Status</label>
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
              <input 
                type="checkbox" 
                name="is_active" 
                id="toggle-active" 
                checked={formData.is_active}
                onChange={handleChange}
                className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer checked:right-0 checked:border-green-500"
                style={{ right: formData.is_active ? '0' : 'auto', left: formData.is_active ? 'auto' : '0' }}
              />
              <label htmlFor="toggle-active" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}></label>
            </div>
          </div>

          <hr className="border-gray-200"/>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Include Image?</label>
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
              <input 
                type="checkbox" 
                name="is_image" 
                id="toggle-image" 
                checked={formData.is_image}
                onChange={handleChange}
                className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer checked:right-0 checked:border-blue-600"
                style={{ right: formData.is_image ? '0' : 'auto', left: formData.is_image ? 'auto' : '0' }}
              />
              <label htmlFor="toggle-image" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${formData.is_image ? 'bg-blue-600' : 'bg-gray-300'}`}></label>
            </div>
          </div>

          <div className={`transition-all duration-300 ${formData.is_image ? 'opacity-100 max-h-60' : 'opacity-50 max-h-0 overflow-hidden'}`}>
            <label className="block text-sm font-medium text-gray-600 mb-2">Upload Image</label>
            <input
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {previewUrl && (
                <div className="mt-2 relative">
                    <p className="text-xs text-gray-400 mb-1">Preview:</p>
                    <img src={previewUrl} alt="Preview" className="h-24 w-auto object-cover rounded border" />
                </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow font-medium transition disabled:opacity-70"
        >
          {loading ? "Processing..." : (initialData ? "Update Notification" : "Create Notification")}
        </button>
      </form>
    </div>
  );
};

// ==========================================
// 2. The Main Page Component
// ==========================================
const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null); // Track which item to edit

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.getAllNotifications.url, {
        method: SummaryApi.getAllNotifications.method,
        headers: {
            Authorization: "Bearer sandeep_university_916",
            "Content-Type": "application/json",
        },
      });
      const result = await response.json();
      
      if (result.status && result.data) {
        setNotifications(result.data);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const getImageUrl = (path) => {
    if (!path) return '';
    const baseUrl = SummaryApi.notifictionImg.url; 
    let fullUrl = `${baseUrl}/${path}`;
    return fullUrl.replace('images/images/', 'images/');
  };

  // Helper function to truncate text (keeps table clean)
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Handlers
  const openCreateModal = () => {
    setEditingNotification(null); // Clear edit data
    setModalIsOpen(true);
  };

  const openEditModal = (notification) => {
    setEditingNotification(notification); // Set data to edit
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setEditingNotification(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Notification Manager</h1>
          <p className="text-gray-500 text-sm">Manage student app notifications</p>
        </div>
        <button 
          onClick={openCreateModal}
          className="mt-4 md:mt-0 flex items-center bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg shadow-md transition transform hover:scale-105"
        >
          <FaPlus className="mr-2" />
          Add Notification
        </button>
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading notifications...</div>
        ) : notifications.length === 0 ? (
           <div className="p-8 text-center text-gray-500">No notifications found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-600 text-sm uppercase tracking-wider">
                  <th className="p-4 border-b">S.No</th>
                  <th className="p-4 border-b">Title</th>
                  <th className="p-4 border-b w-1/3">Description</th>
                  <th className="p-4 border-b">Date Range</th>
                  <th className="p-4 border-b text-center">Image</th>
                  <th className="p-4 border-b text-center">Status</th>
                  <th className="p-4 border-b text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {notifications.map((note, index) => (
                  <tr key={note.notification_id || index} className="hover:bg-gray-50 transition">
                    <td className="p-4 font-medium text-gray-900">{index + 1}</td>
                    <td className="p-4 font-semibold text-gray-800">{note.title}</td>
                    
                    {/* --- DESCRIPTION CELL --- */}
                    <td 
                      className="p-4 text-gray-600 text-sm whitespace-normal break-words max-w-[300px]" 
                      title={note.description}
                    >
                      {truncateText(note.description, 100)} 
                    </td>

                    <td className="p-4 text-sm text-gray-500 whitespace-nowrap">
                       <div className="flex items-center">
                         <FaCalendarAlt className="mr-2 text-gray-400"/>
                         {note.notification_date}
                       </div>
                    </td>
                    <td className="p-4 text-center">
                      {note.is_image === "1" && note.notification_image ? (
                        <a href={getImageUrl(note.notification_image)} target="_blank" rel="noreferrer">
                          <img 
                            src={getImageUrl(note.notification_image)} 
                            alt="thumb" 
                            className="h-10 w-10 object-cover rounded-md mx-auto border hover:scale-150 transition z-10 relative bg-white"
                          />
                        </a>
                      ) : (
                        <span className="text-gray-300"><FaImage size={20} className="mx-auto"/></span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {note.is_active === "1" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <FaCheckCircle className="mr-1"/> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <FaTimesCircle className="mr-1"/> Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                        <button 
                            onClick={() => openEditModal(note)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-full transition"
                            title="Edit Notification"
                        >
                            <FaEdit size={18} />
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="w-full max-w-lg mx-auto mt-20 outline-none p-4"
        overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center overflow-y-auto pt-10 pb-10 z-50"
      >
        <NotificationForm 
          onClose={closeModal} 
          onSuccess={fetchNotifications}
          initialData={editingNotification} // Pass data if editing
        />
      </Modal>

    </div>
  );
};

export default NotificationManager;